import { http, httpBlob } from "@/lib/api";

export type InstallerOs = "windows" | "macos" | "linux";

/** Also the menu order, before the current OS is moved to the front. */
export const INSTALLER_OS_LABELS: Record<InstallerOs, string> = {
  windows: "Windows",
  macos: "Mac",
  linux: "Linux",
};

/** Linux ships in several formats; Windows and Mac each have one installer. */
export type LinuxFormat = "appimage" | "deb" | "rpm";

export const LINUX_FORMATS: { format: LinuxFormat; label: string; hint: string }[] = [
  { format: "appimage", label: "AppImage", hint: "Any distro" },
  { format: "deb", label: ".deb", hint: "Debian, Ubuntu" },
  { format: "rpm", label: ".rpm", hint: "Fedora, openSUSE" },
];

/** What the backend returns for `/api/v1/yggdrasil/updates/installer/{os}`. */
interface InstallerInfo {
  version: string;
  file_name: string;
  /** Short-lived direct link; null when the file has to come through `download_path`. */
  url: string | null;
  download_path: string;
}

/** The OS of the computer this page is open on, when it is one the Support Platform runs on. */
export function detectInstallerOs(): InstallerOs | null {
  const agent = navigator.userAgent;
  if (/Windows/i.test(agent)) return "windows";
  if (/Mac OS X|Macintosh/i.test(agent) && !/iPhone|iPad/i.test(agent)) return "macos";
  if (/Linux|X11/i.test(agent) && !/Android/i.test(agent)) return "linux";
  return null;
}

function saveBlob(blob: Blob, fileName: string) {
  const href = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = href;
  link.download = fileName;
  link.click();
  // Revoking straight away can cancel the download in some browsers.
  setTimeout(() => URL.revokeObjectURL(href), 60_000);
}

/**
 * Downloads the Support Platform (Yggdrasil) desktop installer for an OS (and, on Linux, a format)
 * and returns its version.
 * The scheduler backend only hands installers to signed-in users.
 */
export async function downloadInstaller(os: InstallerOs, format?: LinuxFormat): Promise<string> {
  const query = format ? `?format=${format}` : "";
  const installer = await http<InstallerInfo>(`/api/v1/yggdrasil/updates/installer/${os}${query}`);
  if (installer.url) {
    // A direct link downloads natively in the browser, with its own progress.
    window.location.assign(installer.url);
  } else {
    saveBlob(await httpBlob(installer.download_path), installer.file_name);
  }
  return installer.version;
}
